import React, { FC, useEffect, useState } from "react";

import httpStatusCode from "http-status-codes";
import i18n from "i18next";
import log from "loglevel";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@mui/material";

import { BackdropWithSpinner } from "@/components/atoms";
import { PasswordInput } from "@/components/molecules";
import { Shape } from "@/core/yup";
import { userApi } from "@/shared/apis";
import { URL_EXPIRED } from "@/shared/errors";
import { useDialog, useNavigation } from "@/shared/hooks";

import { PasswordFormType, passwordValidationSchema } from "../../../shared";

type NewPasswordProps = {
    token: string;
    onResetComplete: () => void;
};

export const newPasswordDefaultValues = () => ({
    password: "",
    confirmPassword: "",
});

interface FormType extends PasswordFormType {
    confirmPassword: string;
}

export const newPasswordFormSchema = yup
    .object<{}, Shape<FormType>>({
        confirmPassword: yup.string().oneOf([yup.ref("password"), null], i18n.t("Validations.PasswordMustMatch")),
    })
    .concat(passwordValidationSchema);

const NewPassword: FC<NewPasswordProps> = ({ token, onResetComplete }) => {
    const { t } = useTranslation();
    const { navToRoot } = useNavigation();
    const { errorDialog } = useDialog();
    const [isLoading, setIsLoading] = useState(true);

    const verifyGuid = () => {
        userApi
            .resetPasswordVerifyGuid(token)
            .then(() => log.info("Reset password guid validated"))
            .catch((e: MyBrokerApi.ApiError) => {
                log.warn("complete reset password failed");
                errorDialog({
                    errorCode: e.status === httpStatusCode.GONE ? URL_EXPIRED : e.code,
                });

                navToRoot();
            })
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        verifyGuid();
    }, []);

    const { mutate: completeResetPassword } = useMutation(userApi.resetPasswordComplete, {
        useErrorBoundary: false,
        onSuccess: () => {
            log.info("Reset password with success");
            onResetComplete();
        },
        onError: (e: MyBrokerApi.ApiError) => {
            log.warn("complete reset password failed");
            errorDialog({
                errorCode: e.code,
            });
            navToRoot();
        },
    });

    const {
        handleSubmit,
        control,
        setValue,
        formState: { isValid, isSubmitting },
    } = useForm<FormType>({
        defaultValues: newPasswordDefaultValues(),
        resolver: yupResolver(newPasswordFormSchema),
    });

    const handleResetComplete = (data: FormType) => {
        log.info("complete reset password");
        completeResetPassword({
            resetPasswordGuid: token,
            newPassword: data.password,
            language: i18n.language,
        });
    };

    return (
        <>
            <div className="title">{t("ResetPassword.NewPassword.Title")}</div>
            {isLoading && <BackdropWithSpinner />}
            <form onSubmit={handleSubmit(handleResetComplete)}>
                <PasswordInput
                    name="password"
                    isNew
                    label={t("ResetPassword.NewPassword.YourPassword")}
                    LabelProps={{ className: "mt16" }}
                    placeholder={t("ResetPassword.NewPassword.Placeholder")}
                    control={control}
                    hidePasswordIcon
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setValue("password", event.target.value, { shouldValidate: true });
                    }}
                />
                <br />
                <PasswordInput
                    name="confirmPassword"
                    isNew
                    label={t("Common.ConfirmPassword")}
                    placeholder={t("ResetPassword.NewPassword.Placeholder")}
                    control={control}
                    hidePasswordIcon
                    hideProgressBar
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setValue("confirmPassword", event.target.value, { shouldValidate: true });
                    }}
                />
                <div className="form-bottom-container">
                    <Button
                        data-testid="button_confirm"
                        type="submit"
                        className="mt16"
                        fullWidth
                        disabled={isSubmitting || !isValid}
                    >
                        {t("Common.Confirm")}
                    </Button>
                </div>
            </form>
        </>
    );
};

export default NewPassword;
